import React from 'react';
import PropTypes from 'prop-types';

import { Link } from 'react-scroll';

import { smoothLinkProps } from '@pagerland/common/src/utils';

import Box from '@pagerland/common/src/components/Box';
import Container from '@pagerland/common/src/components/Container';

import Typography from '@pagerland/common/src/components/Typography';
import Button from '@pagerland/common/src/components/Button';
import ParallaxBackground from '../../components/ParallaxBackground';

import data from '../../data';

const WelcomeCourses = ({
  name,
  background,
  title,
  header,
  homepage,
  cta,
  homepageCta,
  homepageSecondaryCta,
  WrapperProps,
  InnerProps,
  ContainerProps,
  CaptionProps,
  TitleProps,
  HeaderProps,
  EyebrowProps,
  CtaProps,
  HomepageCtaGroupProps,
  HomepageSecondaryCtaProps,
}) => (
  <Box name={name} {...WrapperProps}>
    {!homepage && <ParallaxBackground {...background} />}
    <Box {...InnerProps}>
      <Container {...ContainerProps}>
        <Box {...CaptionProps}>
          {homepage && <Typography {...EyebrowProps}>Practical Montessori</Typography>}
          <Typography {...TitleProps} >{title}</Typography>
          <Typography {...HeaderProps} >{header}</Typography>
          {homepage ? (
            <Box {...HomepageCtaGroupProps}>
              <Button {...CtaProps} {...homepageCta} />
              <Button {...CtaProps} {...HomepageSecondaryCtaProps} {...homepageSecondaryCta} />
            </Box>
          ) : (
            <Button {...CtaProps} {...cta} />
          )}
        </Box>
      </Container>
    </Box >
  </Box >
);

WelcomeCourses.propTypes = {
  name: PropTypes.string.isRequired,
  WrapperProps: PropTypes.object,
  InnerProps: PropTypes.object,
  ContainerProps: PropTypes.object,
  CaptionProps: PropTypes.object,
  TitleProps: PropTypes.object,
  HeaderProps: PropTypes.object,
  EyebrowProps: PropTypes.object,
  GridProps: PropTypes.object,
  CtaProps: PropTypes.object,
  FeatureProps: PropTypes.object,
  FeatureIconProps: PropTypes.object,
  FeatureTitleProps: PropTypes.object,
  title: PropTypes.node,
  price: PropTypes.node,
  background: PropTypes.object,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      IconProps: PropTypes.object,
      title: PropTypes.node,
    }),
  ),
  cta: PropTypes.object,
  homepageCta: PropTypes.object,
  homepageSecondaryCta: PropTypes.object,
  HomepageCtaGroupProps: PropTypes.object,
  HomepageSecondaryCtaProps: PropTypes.object,
  homepage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

WelcomeCourses.defaultProps = {
  WrapperProps: {
    pt: 0,
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden',
    borderBottomWidth: '1px',
    borderBottomColor: 'rgba(31, 35, 40, 0.1)',
  },
  InnerProps: {
    pt: {
      _: 34,
      lg: 40,
    },
    pb: {
      _: 18,
      lg: 20,
    },
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    minHeight: {
      _: 156,
      lg: 168,
    },
    bg:
      'radial-gradient(circle at 86% 16%, rgba(213, 170, 72, 0.26) 0%, rgba(213, 170, 72, 0) 44%), radial-gradient(circle at 72% 78%, rgba(255, 255, 255, 0.42) 0%, rgba(255, 255, 255, 0) 40%), linear-gradient(132deg, #f4ecdf 0%, #ece1d0 58%, #f2e8da 100%)',
    zIndex: 2,
  },
  ContainerProps: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  CaptionProps: {
    maxWidth: 700,
    pr: {
      _: 0,
      lg: 4,
    },
  },
  EyebrowProps: {
    as: 'p',
    fontSize: 1,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    mb: 2,
    color: '#7a6335',
    fontWeight: 700,
  },
  TitleProps: {
    as: 'h2',
    variant: 'h2',
    lineHeight: 1.1,
    fontSize: {
      _: 32,
      md: 35,
      lg: 36,
    },
    mb: 2,
    color: '#1f2328',
    children: 'None'
  },
  HeaderProps: {
    variant: 'h5',
    maxWidth: 620,
    lineHeight: 1.55,
    fontWeight: 500,
    fontSize: {
      _: 16,
      lg: 18,
    },
    color: 'rgba(31, 35, 40, 0.86)',
    children: 'None'
  },
  CtaProps: {
    as: Link,
    ...smoothLinkProps,
    mt: 3,
    variant: 'brand',
    size: 'large',
    px: 4,
    fontWeight: 700,
    borderRadius: 6,
  },
  HomepageCtaGroupProps: {
    mt: 3,
    display: 'flex',
    flexDirection: {
      _: 'column',
      md: 'row',
    },
    rowGap: 2,
    columnGap: 3,
    alignItems: {
      _: 'flex-start',
      md: 'center',
    },
  },
  HomepageSecondaryCtaProps: {
    variant: 'default',
    color: 'gray.0',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: 'rgba(31, 35, 40, 0.26)',
    px: 4,
    bg: 'rgba(255, 255, 255, 0.86)',
    fontWeight: 700,
    boxShadow: '0 10px 22px rgba(50, 36, 19, 0.1)',
    borderRadius: 6,
  },
  FeatureProps: {
    flexBox: true,
    alignItems: 'center',
  },
  FeatureIconProps: {
    fontSize: 12,
    color: 'gray.6',
    mr: 2,
  },
  FeatureTitleProps: {
    variant: 'h5',
    color: 'gray.6',
  },
  ...data.welcomecourses,
};

export default WelcomeCourses;
